import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { calibrationKeys, buildPhotoKeys, weighingPhotoKeys, eventReleaseKeys, commonCalsKeys, boatSyncKeys, toeCheckKeys, yawCheckKeys, fleetMeasurementKeys, metricsKeys, platformWeighingKeys, wingWeighingKeys, correctorStateKeys, fleetEqualizationKeys, fleetReportKeys, scheduleKeys } from "@/api/query-keys";
import { asBoatCode, asBoatId, asBuildId, asEventId, asPlatformWeighingId } from "@/types/identifiers";

const RECONNECT_DELAY = 3000;

interface InvalidateMessage {
  type: "invalidate";
  entity:
    | "calibrations"
    | "build-photos"
    | "weighing-photos"
    | "event-releases"
    | "common-cals"
    | "toe-checks"
    | "yaw-checks"
    | "platform-weighings"
    | "wing-weighings"
    | "corrector-states"
    | "schedule"
    | "metrics-snapshots";
  buildId?: string;
  eventId?: string;
  boatCode?: string;
  boatId?: string;
  weighingId?: string;
  config?: string;
}

export function useRealtimeInvalidation() {
  const qc = useQueryClient();

  useEffect(() => {
    // Everything the reconnect chain needs lives in this effect run. A ref
    // shared across runs let StrictMode's remount revive the first run's
    // socket: its close handler saw "mounted" again and started a second,
    // parallel chain, so every reconnect opened two sockets.
    let cancelled = false;
    let ws: WebSocket | null = null;
    let reconnect: ReturnType<typeof setTimeout> | undefined;

    function connect() {
      if (cancelled) return;

      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${proto}//${window.location.host}/ws`);

      ws.onmessage = (event) => {
        try {
          const msg: InvalidateMessage = JSON.parse(event.data);
          if (msg.type !== "invalidate") return;

          switch (msg.entity) {
            case "calibrations":
              if (msg.buildId) qc.invalidateQueries({ queryKey: calibrationKeys.byBuild(asBuildId(msg.buildId)) });
              qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
              qc.invalidateQueries({ queryKey: scheduleKeys.all });
              break;
            case "build-photos":
              if (msg.buildId) qc.invalidateQueries({ queryKey: buildPhotoKeys.byBuild(asBuildId(msg.buildId)) });
              break;
            case "weighing-photos":
              if (msg.weighingId) {
                qc.invalidateQueries({
                  queryKey: weighingPhotoKeys.byWeighing(asPlatformWeighingId(msg.weighingId)),
                });
              }
              break;
            case "event-releases":
              if (msg.eventId) {
                qc.invalidateQueries({ queryKey: eventReleaseKeys.byEvent(asEventId(msg.eventId)) });
                // Target values may have changed — expire all common-cals compare results
                qc.invalidateQueries({ queryKey: commonCalsKeys.teamStatus(asEventId(msg.eventId)) });
                qc.invalidateQueries({ queryKey: boatSyncKeys.bySyncType("common-cals") });
              }
              break;
            case "common-cals":
              if (msg.eventId) {
                qc.invalidateQueries({ queryKey: commonCalsKeys.teamStatus(asEventId(msg.eventId)) });
                if (msg.boatCode) {
                  // Per-boat sync: only the affected boat's compare result is
                  // stale. Each compare hits NVP, so bulk invalidation here
                  // would re-check every team in the fleet for no reason.
                  qc.invalidateQueries({
                    queryKey: boatSyncKeys.compare("common-cals", asBoatCode(msg.boatCode)),
                  });
                } else {
                  // Fallback for older backends that don't send boatCode.
                  qc.invalidateQueries({ queryKey: boatSyncKeys.bySyncType("common-cals") });
                }
              }
              break;
            case "toe-checks":
              if (msg.buildId) qc.invalidateQueries({ queryKey: toeCheckKeys.byBuild(asBuildId(msg.buildId)) });
              qc.invalidateQueries({ queryKey: scheduleKeys.all });
              break;
            case "yaw-checks":
              if (msg.buildId) {
                qc.invalidateQueries({ queryKey: yawCheckKeys.byBuild(asBuildId(msg.buildId)) });
                qc.invalidateQueries({ queryKey: yawCheckKeys.previousEvent(asBuildId(msg.buildId)) });
              }
              qc.invalidateQueries({ queryKey: fleetMeasurementKeys.all });
              qc.invalidateQueries({ queryKey: scheduleKeys.all });
              break;
            case "platform-weighings":
              // Boat-scoped — invalidate the affected boat's per-boat query
              // AND the fleet-wide aggregator (handled by .all prefix match).
              if (msg.boatId) {
                qc.invalidateQueries({ queryKey: platformWeighingKeys.byBoat(asBoatId(msg.boatId)) });
              }
              qc.invalidateQueries({ queryKey: platformWeighingKeys.all });
              // Fleet equalization/report use these as inputs — any view may be stale.
              qc.invalidateQueries({ queryKey: fleetEqualizationKeys.all });
              qc.invalidateQueries({ queryKey: fleetReportKeys.all });
              // A signed-off weighing flips a WEIGH_IN schedule slot to Done.
              qc.invalidateQueries({ queryKey: scheduleKeys.all });
              break;
            case "wing-weighings":
              if (msg.boatId) {
                qc.invalidateQueries({ queryKey: wingWeighingKeys.byBoat(asBoatId(msg.boatId)) });
              }
              qc.invalidateQueries({ queryKey: wingWeighingKeys.all });
              qc.invalidateQueries({ queryKey: fleetEqualizationKeys.all });
              qc.invalidateQueries({ queryKey: fleetReportKeys.all });
              break;
            case "corrector-states":
              if (msg.buildId) {
                qc.invalidateQueries({ queryKey: correctorStateKeys.byBuild(asBuildId(msg.buildId)) });
              } else {
                qc.invalidateQueries({ queryKey: correctorStateKeys.all });
              }
              // Fleet equalization depends on current corrector state for delta calc.
              if (msg.eventId) {
                qc.invalidateQueries({ queryKey: fleetEqualizationKeys.byEvent(asEventId(msg.eventId)) });
              } else {
                qc.invalidateQueries({ queryKey: fleetEqualizationKeys.all });
              }
              qc.invalidateQueries({ queryKey: fleetReportKeys.all });
              break;
            case "schedule":
              if (msg.eventId) {
                qc.invalidateQueries({ queryKey: scheduleKeys.byEvent(asEventId(msg.eventId)) });
              } else {
                qc.invalidateQueries({ queryKey: scheduleKeys.all });
              }
              break;
            case "metrics-snapshots":
              // History/fleet are boat-scoped derivations of the same rows —
              // the .all prefix covers byBuild, history, and fleet keys.
              qc.invalidateQueries({ queryKey: metricsKeys.all });
              break;
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (!cancelled) {
          reconnect = setTimeout(connect, RECONNECT_DELAY);
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnect);
      ws?.close();
    };
  }, [qc]);
}
